/**
 * Okta Authentication Configuration
 *
 * Shared Okta client for CSC Doc Gen Agent.
 * Uses Vite environment variables for runtime configuration.
 */

import { OktaAuth } from '@okta/okta-auth-js';

// Validate required environment variables
const requiredEnvVars = {
  VITE_OKTA_ISSUER: process.env.REACT_APP_OKTA_ISSUER,
  VITE_OKTA_CLIENT_ID: process.env.REACT_APP_OKTA_CLIENT_ID,
  VITE_OKTA_REDIRECT_URI: process.env.REACT_APP_OKTA_REDIRECT_URI,
  VITE_OKTA_REQUIRED_GROUP: process.env.REACT_APP_OKTA_REQUIRED_GROUP,
};

for (const [key, value] of Object.entries(requiredEnvVars)) {
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
}

// Initialize Okta Auth instance
export const oktaAuth = new OktaAuth({
  issuer: process.env.REACT_APP_OKTA_ISSUER,
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
  redirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI,
  scopes: ['openid', 'profile', 'email', 'groups'],
  pkce: true,
  tokenManager: {
    autoRenew: true,
    expireEarlySeconds: 300, // Renew 5 minutes before expiration
    storage: 'localStorage',
  },
});

// Export configuration constants
export const OKTA_CONFIG = {
  issuer: process.env.REACT_APP_OKTA_ISSUER,
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
  redirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI,
  requiredGroup: process.env.REACT_APP_OKTA_REQUIRED_GROUP,
  portalUrl: process.env.REACT_APP_PORTAL_URL,
};

/**
 * Check if user has required group membership
 */
export const hasRequiredGroup = (groups) => {
  const requiredGroup = OKTA_CONFIG.requiredGroup;
  return groups.some(group => group === requiredGroup);
};

/**
 * Get user's identity ID from token claims
 * Uses sub claim as the unique user identifier
 */
export const getUserIdentityId = async () => {
  try {
    const idToken = await oktaAuth.tokenManager.get('idToken');
    return idToken?.claims?.sub || null;
  } catch (error) {
    console.error('Failed to get user identity ID:', error);
    return null;
  }
};

/**
 * Get user's email from token claims
 */
export const getUserEmail = async () => {
  try {
    const idToken = await oktaAuth.tokenManager.get('idToken');
    return idToken?.claims?.email || null;
  } catch (error) {
    console.error('Failed to get user email:', error);
    return null;
  }
};

/**
 * Get user's groups from token claims
 */
export const getUserGroups = async () => {
  try {
    const idToken = await oktaAuth.tokenManager.get('idToken');
    const groups = idToken?.claims?.groups;
    return Array.isArray(groups) ? groups : [];
  } catch (error) {
    console.error('Failed to get user groups:', error);
    return [];
  }
};

/**
 * Get access token for API requests
 */
export const getAccessToken = async () => {
  try {
    const accessToken = await oktaAuth.tokenManager.get('accessToken');
    return accessToken?.accessToken || null;
  } catch (error) {
    console.error('Failed to get access token:', error);
    return null;
  }
};

/**
 * Get ID token JWT string for API Gateway authorization
 */
export const getIdToken = async () => {
  try {
    const idToken = await oktaAuth.tokenManager.get('idToken');
    return idToken?.idToken || null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async () => {
  try {
    return await oktaAuth.isAuthenticated();
  } catch (error) {
    console.error('Failed to check authentication status:', error);
    return false;
  }
};

/**
 * Sign in with redirect to Okta
 */
export const signIn = async () => {
  try {
    await oktaAuth.signInWithRedirect({
      originalUri: window.location.pathname,
    });
  } catch (error) {
    console.error('Failed to initiate sign in:', error);
    throw error;
  }
};

/**
 * Sign out and clear tokens
 * Redirects to Agentic Apps Portal with logged_out flag
 */
export const signOut = async () => {
  try {
    // Clear tokens locally and redirect to portal
    // This avoids Okta's postLogoutRedirectUri configuration requirement
    console.log('Clearing Okta tokens locally...');

    // Clear all tokens from token manager
    await oktaAuth.tokenManager.clear();

    // Redirect to portal
    const portalUrl = OKTA_CONFIG.portalUrl || 'https://dev.apps.aps-ai-eng.adobe.net';
    const redirectUrl = `${portalUrl}/login?logged_out=true`;

    console.log('Redirecting to portal:', redirectUrl);
    window.location.href = redirectUrl;
  } catch (error) {
    console.error('Failed to sign out:', error);
    console.error('Error details:', error.message, error.stack);
    throw error;
  }
};

/**
 * Handle authentication callback
 * Returns true if authentication succeeded and user has required group
 */
export const handleCallback = async () => {
  try {
    // Parse tokens from callback URL
    await oktaAuth.handleRedirect();

    // Verify authentication
    const authenticated = await isAuthenticated();
    if (!authenticated) {
      return { success: false, error: 'Authentication failed' };
    }

    // Check group membership
    const groups = await getUserGroups();
    if (!hasRequiredGroup(groups)) {
      await signOut();
      return {
        success: false,
        error: 'Access denied: You do not have the required group membership'
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Failed to handle authentication callback:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during authentication'
    };
  }
};
